import { useEffect, useState } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { TIP_CATEGORY_LABELS } from "../lib/format";
import { useAuth } from "../context/AuthContext";

export const EMPTY_EMPLOYEE = {
  first_name: "", last_name: "", email: "", phone: "", position: "", status: "active",
  start_date: "", end_date: "", probation_months: "", salary_type: "hourly", rate: "",
  tip_category: "", role: "staff", password: "", pin: "", pos_operator: false,
  id_number: "", bank_name: "", account_number: "", branch_code: "", tax_number: "",
  leave_opening_balance: "", leave_opening_effective_date: "",
};

export const PERMISSION_GROUPS = [
  { key: "capture_invoices", label: "Capture Invoices (upload/OCR)", sets: ["capture_invoices"], def: true },
  { key: "capture_revenue", label: "Capture Revenue & Tips", sets: ["capture_revenue"] },
  { key: "view_financials", label: "View Financial Reports (revenue, tips, dashboard)",
    sets: ["view_financials", "view_tip_distribution", "view_dashboard"] },
  { key: "manage_roster", label: "Manage Rosters & Approve Leave", sets: ["manage_roster", "approve_leave"] },
  { key: "run_payroll", label: "Run Payroll", sets: ["run_payroll"] },
  { key: "manage_stock", label: "Manage Recipes, Menu & Stock", sets: ["manage_stock"] },
  { key: "run_passe", label: "Run Passé Board (service pacing)", sets: ["run_passe"] },
];

export default function EmployeeForm({ initial, onSubmit, submitLabel, isNew }) {
  const { user } = useAuth();
  const [f, setF] = useState({ ...EMPTY_EMPLOYEE, ...(initial || {}) });
  const [perms, setPerms] = useState(() => {
    const existing = (initial || {}).permissions || {};
    const out = {};
    for (const g of PERMISSION_GROUPS) {
      // checked if the group's primary key is explicitly set; new staff default to invoice capture only
      out[g.key] = initial ? !!existing[g.sets[0]] : !!g.def;
    }
    return out;
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...f };
      if (!payload.end_date) payload.end_date = null;
      if (!payload.tip_category) payload.tip_category = null;
      payload.probation_months = payload.probation_months === "" ? null : Number(payload.probation_months);
      payload.leave_opening_balance = payload.leave_opening_balance === "" || payload.leave_opening_balance == null ? null : Number(payload.leave_opening_balance);
      if (!payload.leave_opening_effective_date) payload.leave_opening_effective_date = null;
      payload.rate = Number(payload.rate) || 0;
      if (!isNew && !payload.password) delete payload.password;
      if (!isNew && !payload.pin) delete payload.pin;
      if (user.role === "owner") {
        payload.permissions = {};
        for (const g of PERMISSION_GROUPS) for (const k of g.sets) payload.permissions[k] = !!perms[g.key];
      }
      await onSubmit(payload);
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, props = {}) => (
    <div className="space-y-1.5" key={key}>
      <Label>{label}</Label>
      <Input data-testid={`emp-field-${key}`} value={f[key] ?? ""} onChange={(e) => set(key, e.target.value)} {...props} />
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="employee-form">
      <div>
        <p className="font-heading font-semibold mb-3">Personal</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {field("first_name", "Name", { required: true })}
          {field("last_name", "Surname", { required: true })}
          {field("email", "Email", { type: "email", required: true })}
          {field("phone", "Phone")}
          <div className="space-y-1.5">
            <Label>ID / passport number</Label>
            <Input data-testid="emp-field-id_number" value={f.id_number ?? ""} onChange={(e) => set("id_number", e.target.value)}
              disabled={user.role !== "owner"} placeholder={user.role !== "owner" ? "Owner-only field" : ""} />
          </div>
        </div>
      </div>
      <div>
        <p className="font-heading font-semibold mb-3">Employment</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {field("position", "Position")}
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="emp-field-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="terminated">Terminated</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {field("start_date", "Start date", { type: "date", required: true })}
          {field("end_date", "End date (fixed-term)")}
          {field("probation_months", "Probation (months)", { type: "number", min: 0 })}
          {field("leave_opening_balance", "Opening leave balance (days)", { type: "number", step: "0.25", min: 0, placeholder: "e.g. 12.5 — migrating staff" })}
          {field("leave_opening_effective_date", "Leave balance effective date", { type: "date" })}
          <p className="sm:col-span-2 text-[11px] text-muted-foreground -mt-1">For staff joining MISE with an existing balance: accrual runs forward from the effective date and only leave taken on/after it deducts.</p>
          {user.role === "owner" && (
            <>
              <div className="space-y-1.5">
                <Label>Salary type</Label>
                <Select value={f.salary_type} onValueChange={(v) => set("salary_type", v)}>
                  <SelectTrigger data-testid="emp-field-salary-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="fixed">Fixed (monthly)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {field("rate", f.salary_type === "hourly" ? "Hourly rate (R)" : "Monthly salary (R)", { type: "number", step: "0.01", required: isNew })}
            </>
          )}
          <div className="space-y-1.5">
            <Label>Tip category</Label>
            <Select value={f.tip_category || "none"} onValueChange={(v) => set("tip_category", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="emp-field-tip-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {Object.entries(TIP_CATEGORY_LABELS).map(([k, l]) => (
                  <SelectItem key={k} value={k}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Login role</Label>
            <Select value={f.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="emp-field-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {user.role === "owner" && <SelectItem value="owner">Owner</SelectItem>}
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {field("password", isNew ? "Password" : "New password (blank = keep)", { type: isNew ? "text" : "password", required: isNew, placeholder: "Min 6 characters" })}
          {field("pin", isNew ? "Clock-in PIN (4 digits)" : "New PIN (blank = keep)", { maxLength: 4, required: isNew, placeholder: "4 digits" })}
        </div>
        <label className="flex items-center gap-2 text-sm mt-4">
          <input type="checkbox" data-testid="emp-field-pos-operator" checked={!!f.pos_operator} onChange={(e) => set("pos_operator", e.target.checked)} />
          POS operator — can open tables, build orders and close receipts (staff role only; no financial access)
        </label>
      </div>
      {user.role === "owner" && f.role !== "owner" && (
        <div data-testid="emp-permissions">
          <p className="font-heading font-semibold mb-1">Permissions</p>
          <p className="text-xs text-muted-foreground mb-3">Clock in/out and own leave stay available to everyone regardless of these toggles.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {PERMISSION_GROUPS.map((g) => (
              <label key={g.key} className="flex items-center gap-2 text-sm rounded-md border border-border px-3 py-2 cursor-pointer hover:bg-accent/50 transition-colors">
                <input type="checkbox" data-testid={`emp-perm-${g.key}`} checked={!!perms[g.key]}
                  onChange={(e) => setPerms((p) => ({ ...p, [g.key]: e.target.checked }))} />
                {g.label}
              </label>
            ))}
          </div>
        </div>
      )}
      {user.role === "owner" && (
        <div>
          <p className="font-heading font-semibold mb-1">Banking & tax <span className="text-xs font-normal text-muted-foreground">(encrypted, owner-only)</span></p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
            {field("bank_name", "Bank")}
            {field("account_number", "Account number")}
            {field("branch_code", "Branch code")}
            {field("tax_number", "Tax number")}
          </div>
        </div>
      )}
      <Button type="submit" disabled={saving} data-testid="employee-save-btn"
        className="bg-primary hover:bg-[#8B5CF6] text-white">
        {saving ? "Saving…" : submitLabel}
      </Button>
    </form>
  );
}
